import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, TogglerCheckbox, useToggle } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import Upgrade from 'modules/core/components/Upgrade'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  CustomizationField,
  FieldTypes,
  fieldDescription,
  fieldLabel,
  fieldOptions,
  fieldTypes,
  isCategory,
  isCurrency,
  isExchangeRate,
  isMileageRate,
  isPaymentMethod,
  isPerDiemRates,
  isProjects,
  isSubcategory,
  useExpensesCustomizationUpdate,
} from 'modules/expenses/hooks/useExpensesCustomization'
import CategorySettingsCallout from 'modules/finance/components/CustomCategories/CategorySettingsCallout'
import { missingFeatureRequestReason } from 'modules/finance/lib/missingFeatureRequestReason'
import { UpsellingKit } from 'modules/upselling_kit/components'

import MileageRatesCallout from './MileageRatesCallout'
import Options from './Options'
import PerDiemRatesCallout from './PerDiemRatesCallout'
import useField from './hooks/useField'

interface Props {
  field: CustomizationField
  allFields: CustomizationField[]
}

const FormFieldEditModal = ({ field, allFields }: Props) => {
  const [selectedParent, setSelectedParent] = React.useState<string | undefined>()
  const { mandatory, options } = useField(field, selectedParent)
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { updateField } = useExpensesCustomizationUpdate()
  const [isOpen, toggleOpen] = useToggle(false)

  const onClose = () => {
    navigate(
      navigation.settings.expenses.expensesForms.fields.fullPath({
        resource_type: field.resourceType,
      })
    )
  }

  const [running, setRunning] = React.useState<boolean>(false)

  const editable = field.editableMandatory || field.editableOptions
  const upgradeRequired = !editable && field.upgradeRequired
  const submitEnabled = !upgradeRequired && !running && editable && options.valid

  const parentOptions = (() => {
    if (isPaymentMethod(field) && allFields) return fieldOptions('expense', 'payment', allFields)
    return []
  })()

  const defaultParent = String(parentOptions[0]?.value)

  useEffect(() => {
    setSelectedParent(defaultParent)
  }, [defaultParent])

  const handleSubmit = async () => {
    setRunning(true)

    const response = await updateField({
      companyId: company.id,
      id: field.id,
      mandatory: mandatory.value,
      options: JSON.stringify(options.normalized),
    })

    if (response.errors) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses.settings.customization.update.error'),
      })
    } else {
      onClose()
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses.settings.customization.update.success'),
      })
    }

    setRunning(false)
  }

  const onCloseContact = () => {
    onClose()
    toggleOpen(false)()
  }

  return (
    <Modal
      illustration="pencil"
      title={fieldLabel(field)}
      description={fieldDescription(field)}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        upgradeRequired ? (
          <>
            <Button.Router
              to={navigation.upgradePlan.fullPath()}
              blankTarget
              rel="noopener noreferrer"
              type="secondary"
            >
              {i18n.t('common.explore_plans')}
            </Button.Router>
            <Button onClick={() => toggleOpen(true)()} type="upgrade">
              {i18n.t('packaging.upgrade_plan.buttons.contact_cx')}
            </Button>
          </>
        ) : (
          <>
            <Button type="secondary" onClick={onClose}>
              {i18n.t('expenses_v2.buttons.cancel')}
            </Button>
            <Button disabled={!submitEnabled} submit>
              {i18n.t('expenses_v2.buttons.submit')}
            </Button>
          </>
        )
      }
    >
      <>
        <Box paddingBottom="s8" flexDirection="row" gap="s8" alignItems="center">
          <TogglerCheckbox
            label={i18n.t('expenses_v2.default_fields.mandatory')}
            value={mandatory.value}
            onChange={() => mandatory.set(!mandatory.value)}
            disabled={!field.editableMandatory}
          />
        </Box>
        {!isExchangeRate(field) && !isProjects(field) && (
          <Box paddingY="s8">
            <SelectField
              none={i18n.t('common.none')}
              label={i18n.t('settings.custom_fields.new.labels.field_type')}
              value={field.fieldType}
              options={fieldTypes()}
              readonly
            />
          </Box>
        )}
      </>
      {(isCategory(field) || isSubcategory(field)) && (
        <Box paddingTop="s24">
          <CategorySettingsCallout />
        </Box>
      )}
      {isPaymentMethod(field) && (
        <Box paddingY="s8">
          <SelectField
            label={i18n.t('expenses_v2.forms.fields.payment.label')}
            value={selectedParent}
            options={parentOptions}
            readonly={!field.editableOptions || parentOptions.length <= 1}
            onChange={(value) => setSelectedParent(value)}
          />
        </Box>
      )}
      {isMileageRate(field) && <MileageRatesCallout />}
      {isPerDiemRates(field) && <PerDiemRatesCallout />}
      {field.fieldType === FieldTypes.single_choice &&
        !isCategory(field) &&
        !isSubcategory(field) &&
        !isMileageRate(field) && (
          <Options
            label={fieldLabel(field)}
            options={options.value}
            creatable={field.editableOptions}
            editable={field.editableOptions}
            deletable={field.editableOptions}
            onChange={options.set}
          />
        )}
      {isCurrency(field) && (
        <Box paddingY="s8">
          <TogglerCheckbox
            label={i18n.t('expenses_v2.forms.fields.currency.multi_currency')}
            value
            disabled
          />
        </Box>
      )}
      {upgradeRequired && <Upgrade.Tiny />}
      {isOpen && (
        <UpsellingKit.ProductModal
          isOpen={isOpen}
          onClose={onCloseContact}
          requestSource="Expenses form customization"
          featureRequested={Features.expenses_form_customization}
          moduleRequested={AvailableModules.expenses}
          requestReason={missingFeatureRequestReason(company)}
          pageSource="expenses_form_field_edit_modal"
        />
      )}
    </Modal>
  )
}

export default observer(FormFieldEditModal)
